import toString from 'lodash/toString'
import isString from 'lodash/isString'
import isObject from 'lodash/isObject'
import isNil from 'lodash/isNil'
import isFunction from 'lodash/isFunction'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import type { FunctionComponent, ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { InlineDisplayContent } from './InlineDisplayContent'

import { TextInput } from '../Text/TextInput'
import type { ConfirmDialogOptions, InlineEditorOnClick, UseMutationProps } from '@ztechapps/common.hook'
import { useInlineEditor, useMutation } from '@ztechapps/common.hook'
import { TooltipPopOver } from '@ztechapps/webui.util'
import { InlineEdit } from '../InlineEdit'
import type { FieldMetaProps, FieldProps } from 'formik'
import { gql } from '@apollo/client'

// Dummy mutation for when mutationConfig is undefined but onUpdate is provided
// This allows us to always call useMutation unconditionally (React hooks rule)
const NOOP_MUTATION = gql`
  mutation NoopMutation {
    __typename
  }
`
const NOOP_MUTATION_CONFIG: UseMutationProps = { query: NOOP_MUTATION }

export type InlineInputValidateFunction = (values: any) => Promise<any> | void
export type InlineInputUpdateParamsFunctionResult = {
  input: {
    _id?: string
  } & Record<any, any>
  _id?: string
  filter?: Record<any, any>
} & Record<any, any>
export type InlineInputUpdateParamsFunction = (args: {
  _id?: string
  updatedValue: any
  confirmResult?: any
  name?: string
  initialValues?: any
}) => InlineInputUpdateParamsFunctionResult

// `InlineInputInjectedProps` picks its discard-list from this type, so renaming/removing a key
// here breaks that Pick<> at compile time — but adding a NEW key still requires manually adding it
// to `InlineInputInjectedProps`'s picked-key union and to every leaf that mirrors it (`NumberInput`,
// `CheckboxInput`, `TextAreaInput`), or the new key spreads straight onto their native DOM element.
export type InlineInputContentOptions<T = any> = {
  meta: any
  value: any
  onChange: (e: any) => void
  onKeyDown: (e: any) => void
  onBlur: () => Promise<any>
  submitting: boolean
  inputRef: any
  noLabel: boolean
  saveNow: (args?: any) => Promise<void>
  cancelNow: () => void
  setNewValue: (e: any) => void
  documentId: string
  initialValues?: Record<any, any>
  validate?: InlineInputValidateFunction
  canEdit: boolean
  editable: boolean
  inputProps?: T
}

export type InlineInputEmptyContent =
  | ((onClick: InlineEditorOnClick, options?: InlineInputContentOptions) => ReactNode)
  | ReactNode

export type InlineInputEditContent = ((props: InlineInputContentOptions) => ReactNode) | ReactNode
export type InlineInputDisplayContent =
  | ((initialValue: any, onClick: any, options?: InlineInputContentOptions) => ReactNode)
  | ReactNode
export type InlineInputOnUpdateFunction = (updateParamsResult: {
  input: Record<string, any>
}) => Promise<{ errors: any } | unknown>
export type InlineInputProps<TInputProps extends Record<any, any> = any> = {
  name: string
  initialValues?: Record<any, any>
  mutationConfig?: UseMutationProps
  queryName?: string
  parentInputComponent?: FunctionComponent
  editContent?: InlineInputEditContent
  displayContent?: InlineInputDisplayContent
  emptyContent?: InlineInputEmptyContent
  canEdit?: boolean
  validate?: InlineInputValidateFunction
  updateParams?: InlineInputUpdateParamsFunction
  saveOnEnterKey?: boolean
  hoverToEdit?: boolean
  label?: string | ReactNode
  help?: string | ReactNode
  meta?: FieldMetaProps<any>
  afterUpdate?: ({ input, errors }) => Promise<void>
  inputProps?: TInputProps
  onUpdate?: InlineInputOnUpdateFunction
  confirmBeforeSaveOptions?: ConfirmDialogOptions
  horizontalLayout?: boolean
  horizontalFormLabelWidth?: number
  onEditModeChange?: (isEditMode: boolean) => void
}
export const InlineInput = ({
  editContent,
  displayContent,
  emptyContent,
  canEdit = true,
  afterUpdate,
  name,
  initialValues,
  validate,
  mutationConfig,
  updateParams,
  saveOnEnterKey = true,
  hoverToEdit = true,
  label,
  help,
  meta,
  onUpdate,
  confirmBeforeSaveOptions,
  inputProps,
  horizontalLayout,
  horizontalFormLabelWidth,
  onEditModeChange,
}: InlineInputProps) => {
  // Always call useMutation unconditionally (React hooks rule)
  // When mutationConfig is undefined but onUpdate is provided, use a noop mutation
  const [mutationFn] = useMutation(mutationConfig ?? NOOP_MUTATION_CONFIG)
  // Prefer onUpdate callback over mutation function
  const defaultUpdate = onUpdate ?? mutationFn

  // lodash `get` falls back to the default only for `undefined`, so a GraphQL `null` would reach
  // the editor as the value of a controlled input, which React rejects.
  const initialValue = get(initialValues, name) ?? ''

  const update = async (updatedValue: any, confirmResult?: any) => {
    //if new updatedValue and oldValue are the sme dont update it
    //intentional type unsafe comparison
    // eslint-disable-next-line
    if (updatedValue == initialValue) {
      return
    }

    //run the validation (when  available) and let it throw error if there is any
    //make it as async call
    if (validate) {
      await validate(updatedValue)
    }

    //build the default params
    let updateParamsResult: any = {}
    if (updateParams) {
      updateParamsResult = updateParams({ _id: initialValues._id, updatedValue, confirmResult, name, initialValues })
    } else {
      updateParamsResult = {
        input: {
          _id: initialValues._id,
          [name]: updatedValue,
        },
      }
    }

    //execute the update now and check for gql error, need to cast to object so if undefined it is ok
    const result = (await defaultUpdate(updateParamsResult)) ?? {}
    const errors = (result as { errors?: any })?.errors

    //if there is a callback after the operation, then just return it
    if (afterUpdate) {
      await afterUpdate({ input: updateParamsResult.input, errors })
    }
  }

  const inputRef = useRef(null)
  const {
    editable,
    saving,
    onClick: onClickEditor,
    onKeyDown,
    onBlur,
    newValue,
    setNewValue,
    error,
    saveNow,
    cancelNow,
  } = useInlineEditor(initialValue, update, inputRef, { saveOnEnterKey, confirmBeforeSaveOptions })

  // Call the callback when edit mode changes
  useEffect(() => {
    if (onEditModeChange) {
      onEditModeChange(editable)
    }
  }, [editable, onEditModeChange])

  const onClick: InlineEditorOnClick = () => {
    if (!canEdit) {
      return
    }
    return onClickEditor()
  }

  meta = meta ?? { touched: !!error, error, value: initialValue, initialTouched: true }
  const inlineInputContentOptions: InlineInputContentOptions = {
    ...inputProps,
    editable: false,
    setNewValue,
    meta,
    value: newValue,
    onChange: (e) => setNewValue(e.target.value),
    onKeyDown,
    onBlur,
    submitting: saving,
    inputRef,
    noLabel: true,
    saveNow,
    cancelNow,
    documentId: initialValues?._id,
    initialValues,
    validate,
    canEdit,
    inputProps,
  }
  if (displayContent) {
    displayContent = isFunction(displayContent)
      ? displayContent(initialValue, onClick, inlineInputContentOptions)
      : displayContent
  } else {
    //Default handling of display is string
    displayContent = toString(initialValue)
  }

  displayContent = displayContent as ReactNode

  //set the default empty content if it is not provided
  if (emptyContent) {
    emptyContent = isFunction(emptyContent) ? emptyContent(onClick, inlineInputContentOptions) : emptyContent
  } else {
    emptyContent = <span>---</span>
  }

  if (editContent) {
    editContent = isFunction(editContent) ? editContent(inlineInputContentOptions) : editContent
  } else {
    editContent = <TextInput {...inlineInputContentOptions} />
  }

  const isEmptyValue =
    isNil(initialValue) ||
    (isString(initialValue) && isEmpty(initialValue)) ||
    (isObject(initialValue) && isEmpty(initialValue))
  const formikField = {
    meta,
    form: {},
  } as FieldProps

  const valueContent = isEmptyValue ? emptyContent : displayContent

  //if canEdit is false, show the content with muted styling and disabled cursor to indicate it's not editable
  if (canEdit === false) {
    return (
      <TooltipPopOver tooltipBody="Editing is disabled">
        <span style={{ cursor: 'not-allowed' }}>
          <InlineDisplayContent
            label={label}
            help={help}
            formikField={formikField}
            horizontalLayout={horizontalLayout}
            horizontalFormLabelWidth={horizontalFormLabelWidth}
          >
            {valueContent}
          </InlineDisplayContent>
        </span>
      </TooltipPopOver>
    )
  }

  return (
    <InlineDisplayContent
      label={label}
      help={help}
      formikField={formikField}
      horizontalLayout={horizontalLayout}
      horizontalFormLabelWidth={horizontalFormLabelWidth}
    >
      {editable ? (
        editContent
      ) : hoverToEdit ? (
        <InlineEdit.Display className="mb-0" onClick={onClick}>
          {valueContent}
        </InlineEdit.Display>
      ) : (
        <InlineEdit.DisplayWrapper className="mb-0">{valueContent}</InlineEdit.DisplayWrapper>
      )}
    </InlineDisplayContent>
  )
}
